import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LayoutGrid, List, Plus, Trash2 } from "lucide-react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { clientsApi } from "../api/clientsApi";
import { staffApi, StaffMember } from "../api/staffApi";
import {
  CreateTaskPayload,
  OperatorTask,
  tasksApi,
  UpdateTaskPayload,
} from "../api/tasksApi";
import { Client } from "../types";
import { useAuth } from "../hooks/useAuth";
import { ConfirmModal } from "../components/shared/ConfirmModal";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

const cardClass = "rounded-xl border border-slate-800 bg-slate-900/90 p-4 shadow-lg shadow-black/20";
const selectClass =
  "rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 outline-none ring-blue-500/40 focus:ring-2";
const inputClass =
  "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-blue-500/40 focus:ring-2";

const columns: { key: string; label: string }[] = [
  { key: "ToDo", label: "To Do" },
  { key: "InProgress", label: "In Progress" },
  { key: "Done", label: "Done" },
];

function formatDue(d?: string | null): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
}

function dueInputValue(d?: string | null): string {
  if (!d) return "";
  try {
    const x = new Date(d);
    const y = x.getUTCFullYear();
    const m = String(x.getUTCMonth() + 1).padStart(2, "0");
    const day = String(x.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
}

function isOverdue(task: OperatorTask): boolean {
  if (task.status === "Done" || !task.dueDate) return false;
  const end = new Date(task.dueDate);
  const today = new Date();
  end.setHours(23, 59, 59, 999);
  today.setHours(0, 0, 0, 0);
  return end < today;
}

function priorityClass(p: string): string {
  const x = p.toLowerCase();
  if (x === "urgent" || x === "critical" || x === "high") return "text-red-300 bg-red-950/40 border-red-500/30";
  if (x === "medium") return "text-amber-200 bg-amber-950/35 border-amber-500/25";
  return "text-slate-300 bg-slate-800/60 border-slate-600/40";
}

type ViewMode = "list" | "kanban";

const TaskManagerPage: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<OperatorTask[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [staffFilter, setStaffFilter] = useState("");
  const [sortKey, setSortKey] = useState("dueDate");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const [newClientId, setNewClientId] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newPriority, setNewPriority] = useState("Medium");
  const [newDue, setNewDue] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [saving, setSaving] = useState(false);

  const [detailTask, setDetailTask] = useState<OperatorTask | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPriority, setEditPriority] = useState("Medium");
  const [editStatus, setEditStatus] = useState("ToDo");
  const [editDue, setEditDue] = useState("");
  const [editClientId, setEditClientId] = useState("");
  const [editAssignee, setEditAssignee] = useState("");
  const [detailSaving, setDetailSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, c, s] = await Promise.all([
        tasksApi.getAll({
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(priorityFilter ? { priority: priorityFilter } : {}),
          ...(staffFilter ? { assignedToUserId: staffFilter } : {}),
          sort: sortKey,
        }),
        clientsApi.getAll(),
        staffApi.list(),
      ]);
      setTasks(t);
      setClients(c);
      setStaff(s);
    } catch {
      setError("Could not load tasks.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, staffFilter, sortKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const byColumn = useMemo(() => {
    const map: Record<string, OperatorTask[]> = { ToDo: [], InProgress: [], Done: [] };
    for (const t of tasks) {
      const k = t.status;
      if (map[k]) {
        map[k].push(t);
      } else {
        map.ToDo.push(t);
      }
    }
    return map;
  }, [tasks]);

  const openCreate = () => {
    setNewTitle("");
    setNewDescription("");
    setNewPriority("Medium");
    setNewDue("");
    setNewClientId("");
    const uid = user?.userId;
    setNewAssignee(uid && staff.some((s) => s.id === uid) ? uid : "");
    setModalOpen(true);
  };

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      const payload: CreateTaskPayload = {
        title: newTitle.trim(),
        description: newDescription.trim(),
        priority: newPriority,
        status: "ToDo",
        dueDate: newDue ? new Date(`${newDue}T12:00:00.000Z`).toISOString() : null,
        assignedToUserId: newAssignee || null,
        ...(newClientId ? { clientId: newClientId } : {}),
      };
      await tasksApi.create(payload);
      setModalOpen(false);
      await load();
    } catch {
      setError("Could not create task.");
    } finally {
      setSaving(false);
    }
  };

  const patchTaskStatus = async (id: string, status: string) => {
    try {
      await tasksApi.updateStatus(id, status);
      await load();
    } catch {
      setError("Could not update task.");
    }
  };

  const requestDeleteTask = (id: string) => {
    setDeleteTaskId(id);
    setDeleteConfirmOpen(true);
  };

  const confirmRemoveTask = async () => {
    const id = deleteTaskId;
    setDeleteConfirmOpen(false);
    setDeleteTaskId(null);
    if (!id) {
      return;
    }
    try {
      await tasksApi.remove(id);
      if (detailTask?.id === id) {
        setDetailTask(null);
      }
      await load();
    } catch {
      setError("Could not delete task.");
    }
  };

  const openDetail = (task: OperatorTask) => {
    setDetailTask(task);
    setEditTitle(task.title);
    setEditDescription(task.description ?? "");
    setEditPriority(task.priority);
    setEditStatus(task.status);
    setEditDue(dueInputValue(task.dueDate));
    setEditClientId(task.clientId ?? "");
    setEditAssignee(task.assignedToUserId ?? "");
  };

  const saveDetail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailTask || !editTitle.trim()) return;
    setDetailSaving(true);
    try {
      const body: UpdateTaskPayload = {
        title: editTitle.trim(),
        description: editDescription.trim(),
        priority: editPriority,
        status: editStatus,
        clientId: editClientId || EMPTY_GUID,
        assignedToUserId: editAssignee || EMPTY_GUID,
      };
      if (editDue) {
        body.dueDate = new Date(`${editDue}T12:00:00.000Z`).toISOString();
      }
      await tasksApi.update(detailTask.id, body);
      setDetailTask(null);
      await load();
    } catch {
      setError("Could not save task.");
    } finally {
      setDetailSaving(false);
    }
  };

  const markDone = async () => {
    if (!detailTask) return;
    try {
      await tasksApi.updateStatus(detailTask.id, "Done");
      setDetailTask(null);
      await load();
    } catch {
      setError("Could not update task.");
    }
  };

  if (loading && !tasks.length) {
    return (
      <DashboardLayout>
        <div className={cardClass}>
          <p className="text-sm text-slate-400">Loading…</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-white">Task manager</h1>
            <p className="text-sm text-slate-400">
              List or Kanban — filter by status, priority, and assignee. Tasks can be created without a client.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="mt-5 flex rounded-lg border border-slate-700 p-0.5">
              <button
                type="button"
                title="List view"
                className={`rounded-md px-2 py-1.5 ${viewMode === "list" ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"}`}
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                type="button"
                title="Kanban view"
                className={`rounded-md px-2 py-1.5 ${viewMode === "kanban" ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"}`}
                onClick={() => setViewMode("kanban")}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
            >
              <Plus className="h-4 w-4" />
              Add task
            </button>
          </div>
        </div>

        <div className={`${cardClass} flex flex-wrap gap-3`}>
          <label className="text-xs text-slate-500">
            Status
            <select
              className={`${selectClass} mt-1 block min-w-[9rem]`}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="ToDo">To Do</option>
              <option value="InProgress">In Progress</option>
              <option value="Done">Done</option>
            </select>
          </label>
          <label className="text-xs text-slate-500">
            Priority
            <select
              className={`${selectClass} mt-1 block min-w-[9rem]`}
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Urgent">Urgent</option>
            </select>
          </label>
          <label className="text-xs text-slate-500">
            Assigned to
            <select
              className={`${selectClass} mt-1 block min-w-[11rem]`}
              value={staffFilter}
              onChange={(e) => setStaffFilter(e.target.value)}
            >
              <option value="">All staff</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.firstName} {s.lastName}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-500">
            Sort
            <select
              className={`${selectClass} mt-1 block min-w-[9rem]`}
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
            >
              <option value="dueDate">Due date</option>
              <option value="priority">Priority</option>
              <option value="createdAt">Created date</option>
            </select>
          </label>
        </div>

        {error ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-950/30 px-4 py-3 text-sm text-amber-100">{error}</div>
        ) : null}

        {viewMode === "list" ? (
          <div className={`${cardClass} overflow-x-auto`}>
            {tasks.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-500">No tasks match the current filters.</p>
            ) : (
              <table className="w-full min-w-[720px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase text-slate-500">
                    <th className="pb-2 pr-3 font-medium">Title</th>
                    <th className="pb-2 pr-3 font-medium">Assigned to</th>
                    <th className="pb-2 pr-3 font-medium">Client</th>
                    <th className="pb-2 pr-3 font-medium">Due</th>
                    <th className="pb-2 pr-3 font-medium">Priority</th>
                    <th className="pb-2 pr-3 font-medium">Status</th>
                    <th className="pb-2 font-medium w-24" />
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => {
                    const overdue = isOverdue(task);
                    return (
                      <tr
                        key={task.id}
                        className="border-b border-slate-800/80 hover:bg-slate-950/50 cursor-pointer"
                        onClick={() => openDetail(task)}
                      >
                        <td className="py-2 pr-3 font-medium text-white">{task.title}</td>
                        <td className="py-2 pr-3 text-slate-400">{task.assignedStaffName ?? "—"}</td>
                        <td className="py-2 pr-3 text-slate-400">{task.clientName?.trim() || "—"}</td>
                        <td className={`py-2 pr-3 ${overdue ? "text-red-400" : "text-slate-400"}`}>
                          {formatDue(task.dueDate)}
                          {overdue ? " (overdue)" : ""}
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`rounded border px-2 py-0.5 text-[10px] uppercase ${priorityClass(task.priority)}`}>
                            {task.priority}
                          </span>
                        </td>
                        <td className="py-2 pr-3" onClick={(e) => e.stopPropagation()}>
                          <select
                            className={selectClass}
                            value={task.status}
                            onChange={(e) => void patchTaskStatus(task.id, e.target.value)}
                          >
                            <option value="ToDo">To Do</option>
                            <option value="InProgress">In Progress</option>
                            <option value="Done">Done</option>
                          </select>
                        </td>
                        <td className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="text-slate-500 hover:text-red-400"
                            aria-label="Delete task"
                            onClick={() => void requestDeleteTask(task.id)}
                          >
                            <Trash2 className="h-4 w-4 inline" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-3">
            {columns.map((col) => (
              <div key={col.key} className={`${cardClass} flex flex-col`}>
                <h2 className="mb-3 border-b border-slate-800 pb-2 text-sm font-semibold text-slate-200">{col.label}</h2>
                <div className="flex flex-1 flex-col gap-2">
                  {byColumn[col.key]?.length === 0 ? (
                    <p className="py-8 text-center text-xs text-slate-500">No tasks</p>
                  ) : (
                    byColumn[col.key].map((task) => {
                      const overdue = isOverdue(task);
                      return (
                        <div
                          key={task.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => openDetail(task)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openDetail(task);
                            }
                          }}
                          className={`rounded-lg border bg-slate-950/80 p-3 text-left cursor-pointer ${
                            overdue ? "border-red-500/70 ring-1 ring-red-500/40" : "border-slate-800"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-white">{task.title}</p>
                            <button
                              type="button"
                              className="text-slate-500 hover:text-red-400 shrink-0"
                              aria-label="Delete task"
                              onClick={(e) => {
                                e.stopPropagation();
                                void requestDeleteTask(task.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="mt-1 text-xs text-slate-400">{task.clientName?.trim() || "—"}</p>
                          {task.description ? (
                            <p className="mt-2 line-clamp-3 text-xs text-slate-500">{task.description}</p>
                          ) : null}
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className={`rounded border px-2 py-0.5 text-[10px] uppercase ${priorityClass(task.priority)}`}>
                              {task.priority}
                            </span>
                            <span className="text-[11px] text-slate-500">
                              Due {formatDue(task.dueDate)}
                              {overdue ? <span className="ml-1 font-semibold text-red-400">(overdue)</span> : null}
                            </span>
                          </div>
                          <p className="mt-1 text-[11px] text-slate-500">
                            {task.assignedStaffName ? (
                              <>
                                <span className="text-slate-600">Assigned:</span> {task.assignedStaffName}
                              </>
                            ) : (
                              <span className="italic text-slate-600">Unassigned</span>
                            )}
                          </p>
                          <label className="mt-2 block text-[10px] uppercase text-slate-600" onClick={(e) => e.stopPropagation()}>
                            Move to
                            <select
                              className={`${selectClass} mt-1 w-full`}
                              value={task.status}
                              onChange={(e) => void patchTaskStatus(task.id, e.target.value)}
                            >
                              <option value="ToDo">To Do</option>
                              <option value="InProgress">In Progress</option>
                              <option value="Done">Done</option>
                            </select>
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {modalOpen ? (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 sm:items-center">
            <div className={`${cardClass} max-h-[90vh] w-full max-w-lg overflow-y-auto border-slate-700`}>
              <h3 className="text-lg font-semibold text-white">New task</h3>
              <form className="mt-4 space-y-3" onSubmit={(e) => void submitCreate(e)}>
                <label className="block text-xs text-slate-500">
                  Client (optional)
                  <select className={inputClass} value={newClientId} onChange={(e) => setNewClientId(e.target.value)}>
                    <option value="">— None —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.firstName} {c.lastName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs text-slate-500">
                  Title
                  <input className={inputClass} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} required />
                </label>
                <label className="block text-xs text-slate-500">
                  Description
                  <textarea
                    className={`${inputClass} min-h-[80px]`}
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                  />
                </label>
                <label className="block text-xs text-slate-500">
                  Priority
                  <select className={inputClass} value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </label>
                <label className="block text-xs text-slate-500">
                  Due date
                  <input type="date" className={inputClass} value={newDue} onChange={(e) => setNewDue(e.target.value)} />
                </label>
                <label className="block text-xs text-slate-500">
                  Assign to
                  <select className={inputClass} value={newAssignee} onChange={(e) => setNewAssignee(e.target.value)}>
                    <option value="">—</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.firstName} {s.lastName}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    className="w-full rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 sm:w-auto"
                    onClick={() => setModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-40 sm:w-auto"
                  >
                    {saving ? "Saving…" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        {detailTask ? (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 sm:items-center">
            <div className={`${cardClass} max-h-[90vh] w-full max-w-lg overflow-y-auto border-slate-700`}>
              <h3 className="text-lg font-semibold text-white">Task details</h3>
              <form className="mt-4 space-y-3" onSubmit={(e) => void saveDetail(e)}>
                <label className="block text-xs text-slate-500">
                  Client (optional)
                  <select className={inputClass} value={editClientId} onChange={(e) => setEditClientId(e.target.value)}>
                    <option value="">— None —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.firstName} {c.lastName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs text-slate-500">
                  Title
                  <input className={inputClass} value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required />
                </label>
                <label className="block text-xs text-slate-500">
                  Description
                  <textarea
                    className={`${inputClass} min-h-[80px]`}
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                </label>
                <label className="block text-xs text-slate-500">
                  Priority
                  <select className={inputClass} value={editPriority} onChange={(e) => setEditPriority(e.target.value)}>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </label>
                <label className="block text-xs text-slate-500">
                  Status
                  <select className={inputClass} value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                    <option value="ToDo">To Do</option>
                    <option value="InProgress">In Progress</option>
                    <option value="Done">Done</option>
                  </select>
                </label>
                <label className="block text-xs text-slate-500">
                  Due date
                  <input type="date" className={inputClass} value={editDue} onChange={(e) => setEditDue(e.target.value)} />
                </label>
                <label className="block text-xs text-slate-500">
                  Assign to
                  <select className={inputClass} value={editAssignee} onChange={(e) => setEditAssignee(e.target.value)}>
                    <option value="">—</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.firstName} {s.lastName}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                    <button
                      type="button"
                      className="w-full rounded-lg border border-emerald-600/50 px-3 py-2 text-sm text-emerald-200 hover:bg-emerald-950/40 sm:w-auto"
                      onClick={() => void markDone()}
                    >
                      Mark complete
                    </button>
                    <button
                      type="button"
                      className="w-full rounded-lg border border-red-600/50 px-3 py-2 text-sm text-red-300 hover:bg-red-950/30 sm:w-auto"
                      onClick={() => void requestDeleteTask(detailTask.id)}
                    >
                      Delete
                    </button>
                  </div>
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-2">
                    <button
                      type="button"
                      className="w-full rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 sm:w-auto"
                      onClick={() => setDetailTask(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={detailSaving}
                      className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-40 sm:w-auto"
                    >
                      {detailSaving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        <ConfirmModal
          isOpen={deleteConfirmOpen}
          title="Delete Task"
          message="Are you sure you want to delete this task? This action cannot be undone."
          confirmLabel="Delete"
          confirmVariant="danger"
          onConfirm={() => void confirmRemoveTask()}
          onCancel={() => {
            setDeleteConfirmOpen(false);
            setDeleteTaskId(null);
          }}
        />
      </div>
    </DashboardLayout>
  );
};

export default TaskManagerPage;
